import type { api } from "@breakout/backend/convex/_generated/api"
import { secToMs } from "@breakout/shared/utils/time"
import { NumberTicker } from "@breakout/ui/components/NumberTicker"
import { cn, formatCompactUsd } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { FunctionReturnType } from "convex/server"
import { ChevronDown, ExternalLink } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { memo, useMemo } from "react"
import type { MarketRouteState } from "../../routes/markets/$marketId"
import { CountdownBadge } from "../ui/CountdownBadge"
import { SimpleAvatar } from "../ui/SimpleAvatar"

// =============================================================================
// Types (inferred from backend query)
// =============================================================================

type WeekViewData = NonNullable<FunctionReturnType<typeof api.public.home.getWeekViewData>>
type Creator = WeekViewData["creators"][number]
type FFGMarket = NonNullable<WeekViewData["ffgMarket"]>
type WeekData = WeekViewData["week"]

export type SortField = "mindshare" | "liquidity"
export type SortDirection = "asc" | "desc"

export interface WeekViewProps {
	week: WeekData
	creators: Creator[]
	ffgMarket: FFGMarket | null
	sortField?: SortField
	sortDirection?: SortDirection
	onSortChange?: (field: SortField, direction: SortDirection) => void
}

// =============================================================================
// Main Component
// =============================================================================

export function WeekView({
	week,
	creators,
	ffgMarket,
	sortField,
	sortDirection,
	onSortChange,
}: WeekViewProps) {
	// Cutoff time for countdown badge
	const cutoffMs = week.cutoffTimeSec ? secToMs(week.cutoffTimeSec) : null

	// Format date helper
	const formatDate = (timestampSec: number) => {
		if (!timestampSec || timestampSec <= 0) {
			return "—"
		}
		const date = new Date(secToMs(timestampSec))
		if (Number.isNaN(date.getTime())) {
			return "—"
		}
		return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
	}

	// Current sort state (defaults: liquidity desc)
	const currentSort = sortField ?? "liquidity"
	const currentDirection = sortDirection ?? "desc"

	// Sort creators based on current sort state
	const sortedCreators = useMemo(() => {
		return [...creators].sort((a, b) => {
			let cmp = 0
			switch (currentSort) {
				case "mindshare":
					cmp = a.mindsharePct - b.mindsharePct
					break
				case "liquidity":
					cmp = a.liquidity - b.liquidity
					break
			}
			return currentDirection === "asc" ? cmp : -cmp
		})
	}, [creators, currentSort, currentDirection])

	// Handle sort click
	const handleSort = (field: SortField) => {
		if (!onSortChange) {
			return
		}

		if (field === currentSort) {
			// Toggle direction
			onSortChange(field, currentDirection === "asc" ? "desc" : "asc")
		} else {
			// New field defaults to desc
			onSortChange(field, "desc")
		}
	}

	return (
		<div className="grid min-w-0 gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
			{/* Featured - shows first on mobile, sidebar on desktop */}
			<aside className="lg:order-2">
				{ffgMarket ? (
					<motion.div
						animate={{ opacity: 1, scale: 1 }}
						initial={{ opacity: 0, scale: 0.97 }}
						transition={{ duration: 0.35, ease: "easeOut" }}
					>
						<FeaturedMarketCard ffgMarket={ffgMarket} openTimeSec={week.openTimeSec} />
					</motion.div>
				) : null}
			</aside>

			{/* Leaderboard - main content, shows second on mobile */}
			<section className="min-w-0 space-y-2 lg:order-1">
				{/* Header: title with date + status + IPFS links */}
				<div className="flex flex-wrap items-center justify-between gap-2 px-1">
					<div className="flex items-center gap-2">
						<h3 className="font-bold text-lg text-text-primary">
							{creators.length > 0 ? "CT Mindshare" : "Markets"}
						</h3>
						<span className="text-text-muted">·</span>
						<Link
							className="text-sm text-text-muted hover:text-text-primary hover:underline"
							to="/weeks"
						>
							{formatDate(week.openTimeSec)} - {formatDate(week.cutoffTimeSec)}
						</Link>
						<CountdownBadge size="sm" targetMs={cutoffMs} />
					</div>
					<div className="flex items-center gap-2 text-xs">
						{week.startSnapshotIpfsCid && (
							<a
								aria-label="View start snapshot on IPFS"
								className="flex items-center gap-0.5 text-text-muted hover:text-text-secondary"
								href={`https://gateway.pinata.cloud/ipfs/${week.startSnapshotIpfsCid}`}
								rel="noopener noreferrer"
								target="_blank"
							>
								<ExternalLink aria-hidden="true" className="size-3" />
								<span className="hidden sm:inline">Start</span>
							</a>
						)}
						{week.endSnapshotIpfsCid && (
							<a
								aria-label="View end snapshot on IPFS"
								className="flex items-center gap-0.5 text-text-muted hover:text-text-secondary"
								href={`https://gateway.pinata.cloud/ipfs/${week.endSnapshotIpfsCid}`}
								rel="noopener noreferrer"
								target="_blank"
							>
								<ExternalLink aria-hidden="true" className="size-3" />
								<span className="hidden sm:inline">End</span>
							</a>
						)}
					</div>
				</div>

				{/* Column Headers */}
				{creators.length > 0 && (
					<div className="flex items-center gap-2.5 px-3 lg:gap-5 lg:px-6">
						<div className="flex-1 font-bold text-[10px] text-text-muted uppercase lg:text-xs">
							Creator
						</div>
						<SortableHeader
							className="shrink-0"
							currentDirection={currentDirection}
							currentSort={currentSort}
							field="mindshare"
							label="Mindshare"
							mobileLabel="Mindshare"
							onSort={handleSort}
						/>
						<SortableHeader
							className="w-14 justify-center pl-3 lg:w-20 lg:pl-4"
							currentDirection={currentDirection}
							currentSort={currentSort}
							field="liquidity"
							label="Liquidity"
							mobileLabel="Liq"
							onSort={handleSort}
						/>
					</div>
				)}

				{creators.length === 0 ? (
					<div className="rounded-2xl border border-border border-dashed bg-bg-card py-16 text-center shadow-sm">
						<p className="font-medium text-text-secondary">New markets coming soon</p>
						<p className="mt-2 text-sm text-text-muted">
							Check back later for the next round of prediction markets.
						</p>
					</div>
				) : (
					<AnimatePresence mode="wait">
						<motion.div
							animate={{ opacity: 1 }}
							className="min-w-0 space-y-2"
							exit={{ opacity: 0 }}
							initial={{ opacity: 0 }}
							key={`${currentSort}-${currentDirection}`}
							transition={{ duration: 0.15 }}
						>
							{sortedCreators.map(creator => (
								<div key={creator.id}>
									<CreatorRow creator={creator} openTimeSec={week.openTimeSec} />
								</div>
							))}
						</motion.div>
					</AnimatePresence>
				)}
			</section>
		</div>
	)
}

// =============================================================================
// Subcomponents (single-use, colocated)
// =============================================================================

interface SortableHeaderProps {
	label: string
	mobileLabel: string
	field: SortField
	currentSort: SortField
	currentDirection: SortDirection
	onSort: (field: SortField) => void
	className?: string
}

function SortableHeader({
	label,
	mobileLabel,
	field,
	currentSort,
	currentDirection,
	onSort,
	className,
}: SortableHeaderProps) {
	const isActive = currentSort === field

	return (
		<button
			className={cn(
				"flex shrink-0 items-center gap-0.5 font-bold text-[10px] uppercase transition-colors lg:text-xs",
				isActive ? "text-text-primary" : "text-text-muted hover:text-text-secondary",
				className,
			)}
			onClick={() => onSort(field)}
			type="button"
		>
			<span className="hidden lg:inline">{label}</span>
			<span className="lg:hidden">{mobileLabel}</span>
			<motion.span
				animate={{
					rotate: isActive && currentDirection === "asc" ? 180 : 0,
					opacity: isActive ? 1 : 0.4,
				}}
				transition={{ duration: 0.2 }}
			>
				<ChevronDown className="size-3" />
			</motion.span>
		</button>
	)
}

interface CreatorRowProps {
	creator: Creator
	openTimeSec: number
}

const CreatorRow = memo(function CreatorRow({ creator, openTimeSec }: CreatorRowProps) {
	// Format liquidity display using same formatter as market page
	const liquidity = creator.liquidity
	const renderLiquidity = () => {
		if (liquidity > 0) {
			return <NumberTicker format={formatCompactUsd} value={liquidity} />
		}
		return "—"
	}

	// Determine direction and display odds for that direction
	const isUp = creator.upPercent >= 50
	const isDown = creator.upPercent < 50
	// Show odds for the displayed direction: up shows upPercent, down shows downPercent
	const displayPercent = isUp ? creator.upPercent : 100 - creator.upPercent

	// Pass data to market page to avoid flash on navigation
	const routeState: MarketRouteState = {
		name: creator.name,
		handle: creator.handle,
		avatarUrl: creator.avatarUrl,
		upPercent: creator.upPercent,
		liquidity: creator.liquidity,
		rank: creator.rank,
		mindsharePct: creator.mindsharePct,
		title: creator.title,
		outcomeLabels: creator.outcomeLabels,
		cutoffTimeSec: creator.cutoffTimeSec,
		openTimeSec,
		marketType: "binary",
	}

	return (
		<Link
			className="group flex w-full min-w-0 items-center gap-2.5 rounded-2xl border border-transparent bg-bg-card px-3 py-2.5 text-left shadow-sm ring-1 ring-border-subtle transition-all duration-200 hover:-translate-y-0.5 hover:border-breakout-brand/20 hover:bg-bg-card hover:shadow-md hover:ring-breakout-brand/30 active:scale-[0.99] lg:gap-5 lg:px-6 lg:py-4 dark:bg-bg-secondary/40 dark:hover:bg-bg-card"
			data-handle={creator.handle}
			data-testid="creator-row"
			params={{ marketId: creator.marketAddress }}
			state={routeState}
			to="/markets/$marketId"
		>
			{/* Avatar - view-transition-name enables morph animation to market page */}
			<SimpleAvatar
				alt={creator.name}
				className="shrink-0 lg:size-11"
				size="md"
				src={creator.avatarUrl}
				style={{ viewTransitionName: `market-avatar-${creator.marketAddress}` }}
				variant="square"
			/>

			{/* Name and handle */}
			<div className="min-w-0 flex-1">
				<span className="block truncate font-bold text-base text-text-primary transition-colors group-hover:text-breakout-brand lg:text-lg">
					{creator.name}
				</span>
				<span className="block truncate font-medium text-sm text-text-muted transition-colors group-hover:text-text-secondary lg:text-sm">
					@{creator.handle}
				</span>
			</div>

			{/* Mindshare */}
			<div className="flex shrink-0 flex-col items-center justify-center text-center">
				<div className="font-mono font-semibold text-sm text-text-primary tabular-nums lg:text-base">
					{creator.mindsharePct > 0 ? (
						<>
							<NumberTicker value={creator.mindsharePct} />%
						</>
					) : (
						"—"
					)}
				</div>
			</div>

			{/* Probability */}
			<div className="flex w-14 shrink-0 flex-col items-center justify-center border-border-highlight border-l pl-3 text-center lg:w-20 lg:pl-4">
				<div
					className={`flex items-center justify-center gap-0.5 font-bold font-mono text-sm tabular-nums lg:gap-1 lg:text-lg ${
						isUp ? "text-text-positive" : isDown ? "text-text-negative" : "text-text-muted"
					}`}
				>
					{isUp && (
						<span aria-hidden="true" className="translate-y-[-1px]">
							▲
						</span>
					)}
					{isDown && (
						<span aria-hidden="true" className="translate-y-[-1px]">
							▼
						</span>
					)}
					{displayPercent > 99 ? (
						">99%"
					) : displayPercent < 1 ? (
						"<1%"
					) : (
						<>
							<NumberTicker decimals={0} value={Math.round(displayPercent)} />%
						</>
					)}
				</div>
				<span className="block text-[10px] text-text-muted lg:text-xs">{renderLiquidity()}</span>
			</div>
		</Link>
	)
})

interface FeaturedMarketCardProps {
	ffgMarket: FFGMarket
	openTimeSec: number
}

const FeaturedMarketCard = memo(function FeaturedMarketCard({
	ffgMarket,
	openTimeSec,
}: FeaturedMarketCardProps) {
	const isBinary = ffgMarket.marketType === "binary"

	// Pass data to market page to avoid flash on navigation
	const routeState: MarketRouteState = {
		name: ffgMarket.displayName,
		avatarUrl: ffgMarket.avatarUrl,
		liquidity: ffgMarket.liquidity,
		title: ffgMarket.title,
		outcomeLabels: ffgMarket.outcomeLabels,
		cutoffTimeSec: ffgMarket.cutoffTimeSec,
		openTimeSec,
		marketType: ffgMarket.marketType,
	}

	return (
		<Link
			className="block w-full rounded-2xl border border-breakout-secondary/20 bg-bg-card px-5 py-4 text-center shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:border-breakout-secondary/40 hover:shadow-xl sm:rounded-3xl sm:p-5 lg:p-6"
			data-testid="featured-market-card"
			params={{ marketId: ffgMarket.marketAddress }}
			state={routeState}
			to="/markets/$marketId"
		>
			{/* Countdown badge */}
			<div className="mb-2 flex justify-center sm:mb-4">
				<CountdownBadge size="sm" targetMs={secToMs(ffgMarket.cutoffTimeSec)} />
			</div>

			{/* Title */}
			{isBinary ? (
				<div className="flex flex-col items-center gap-1.5 sm:gap-2">
					<div className="flex items-center justify-center gap-2">
						{ffgMarket.avatarUrl && (
							<img
								alt=""
								aria-hidden="true"
								className="size-7 rounded-lg sm:size-9"
								src={ffgMarket.avatarUrl}
							/>
						)}
						<h3 className="font-extrabold text-xl text-text-primary leading-tight sm:text-3xl sm:leading-tight">
							{ffgMarket.displayName}
						</h3>
					</div>
					{ffgMarket.title !== ffgMarket.displayName && (
						<p className="text-balance font-semibold text-base text-text-secondary leading-snug sm:text-lg sm:leading-snug">
							{ffgMarket.title}
						</p>
					)}
				</div>
			) : ffgMarket.winningBucket !== null ? (
				<h3 className="text-balance font-extrabold text-xl text-text-primary leading-tight sm:text-3xl sm:leading-none">
					Winner:{" "}
					<span className="text-breakout-secondary">
						{ffgMarket.outcomeLabels[ffgMarket.winningBucket] ??
							`Bucket ${ffgMarket.winningBucket}`}
					</span>
				</h3>
			) : (
				<div className="flex flex-col items-center gap-1.5 sm:gap-2">
					<img
						alt=""
						aria-hidden="true"
						className="h-7 w-8 sm:h-9 sm:w-11"
						src="/fallfromgrace.png"
					/>
					<h3 className="text-balance font-extrabold text-xl text-text-primary leading-tight sm:text-3xl sm:leading-tight">
						{ffgMarket.title}
					</h3>
				</div>
			)}

			{/* Contenders (FFG only) */}
			<div className="mt-2 sm:mt-4">
				{isBinary ? null : (
					<>
						{/* Mobile: avatars only in a single line */}
						<div className="flex justify-center gap-2.5 sm:hidden">
							{ffgMarket.top5.map(({ label, avatarUrl }) => (
								<SimpleAvatar
									alt={label}
									className="size-9"
									key={label}
									size="sm"
									src={avatarUrl}
									variant="square"
								/>
							))}
						</div>

						{/* Desktop: chips with labels */}
						<div className="hidden flex-wrap justify-center gap-1.5 sm:flex">
							{ffgMarket.top5.map(({ label, avatarUrl }) => (
								<div
									className="group/chip flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-card py-1 pr-2.5 pl-1 shadow-sm transition-colors hover:border-breakout-secondary/30 hover:bg-bg-secondary dark:border-transparent dark:bg-transparent dark:shadow-none dark:hover:bg-bg-secondary/40"
									key={label}
								>
									<SimpleAvatar
										alt={label}
										className="size-8 ring-1 ring-border-highlight group-hover/chip:ring-breakout-secondary/30"
										size="sm"
										src={avatarUrl}
										variant="square"
									/>
									<span className="font-semibold text-text-secondary text-xs group-hover/chip:text-text-primary">
										{label}
									</span>
								</div>
							))}
						</div>
					</>
				)}

				{/* Liquidity */}
				{ffgMarket.liquidity > 0 && (
					<p className="mt-3 font-semibold text-sm text-text-muted sm:mt-4 sm:text-base">
						{formatCompactUsd(ffgMarket.liquidity)} Liq.
					</p>
				)}
			</div>
		</Link>
	)
})
